import { action, computed, observable, makeObservable } from 'mobx';
import { defaultNotificationSettingFilters, NotificationsSettingPath } from '../constants/notifications';
import { notificationSettingCast } from '../helpers/notifications';
import { INotificationSetting, INotificationsSettingFilters, IResponseNotificationSetting } from '../types/notifications';
import { defaultPagination } from 'src/constants/main';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { IPagination, LoadingStatus, TBooleanInNumber } from 'src/types/main';
import { booleanNumberReverse } from 'src/utils/helpers';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

export default class NotificationsSettingStore {
  private path;
  private returnPath;

  constructor(path: NotificationsSettingPath, returnPath: string) {
    makeObservable(this);
    this.path = path;
    this.returnPath = returnPath;
  }

  private fetchAbortController = getAbortController();
  private cancelStack: Record<string, AbortController> = {};

  @observable pagination: IPagination<INotificationSetting> = defaultPagination;
  @observable filters: INotificationsSettingFilters = defaultNotificationSettingFilters;

  @observable loading: LoadingStatus = LoadingStatus.Init;

  @action setFilters = (filters: Partial<INotificationsSettingFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchNotifications(1);
  };

  @computed get isLoading(): boolean {
    return this.loading === LoadingStatus.Loading;
  }

  @action fetchNotifications = async (pageNumber: number = 1) => {
    this.fetchAbortController.abort();
    this.fetchAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const params = {
        page: pageNumber,
        sort_field: this.filters.sortField || undefined,
        sort_dir: this.filters.sortDir || undefined,
        is_delete: 0,
      };

      const response: IPagination<IResponseNotificationSetting> = await client.get(`v3/settings/notifications/${this.path}`, {
        params,
        signal: this.fetchAbortController.signal,
      });

      this.pagination = { ...response, data: response.data.map((item) => notificationSettingCast(item)) };
      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'NotificationsSettingStore fetchNotifications', showModal: true });
    }
  };

  @action changeNotificationStatus = async (id: number, status: TBooleanInNumber) => {
    const cancelId = `notification-status_${id}`;

    try {
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId].abort();
      this.cancelStack[cancelId] = getAbortController();
      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status } : item));

      const response: IResponseNotificationSetting[] = await client.put(
        `v3/settings/notifications/${this.path}/${id}`,
        { status },
        { signal: this.cancelStack[cancelId].signal }
      );

      const responseNotification: IResponseNotificationSetting | undefined = response.length ? response[0] : undefined;

      if (responseNotification && responseNotification.auto === 1) {
        this.pagination.data = this.pagination.data.map((item) => {
          if (responseNotification.id === item.id) return item;
          return item.event === responseNotification.order_status ? { ...item, status: 0 } : item;
        });
      }
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status: booleanNumberReverse(item.status) } : item));
      logger.error({ error, place: 'NotificationsSettingStore changeNotificationStatus' });
    }
  };

  @action deleteNotification = async (id: number) => {
    try {
      this.loading = LoadingStatus.Loading;

      await client.delete(`v3/settings/notifications/${this.path}/${id}`);

      this.loading = LoadingStatus.Success;
      this.fetchNotifications(this.pagination.current_page);
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'NotificationsSettingStore deleteNotification', showModal: true });
    }
  };

  public clearFilters = () => {
    if (!history.location.pathname.includes(this.returnPath)) this.filters = defaultNotificationSettingFilters;
  };
}
